import React from 'react';
import { FEELS } from '../../../vibe/definitions';
import {
  NEUTRAL_NOTE_COLOR,
  NEUTRAL_ORB_COLOR,
  NOTE_SYMBOLS,
  orbCoreBackground,
  orbHalo,
  orbNotePositions,
} from '../../../vibe/orbVisual';

const NOTE_LAYOUT = orbNotePositions(NOTE_SYMBOLS.length, 36);

function moveFeel(currentId, delta) {
  const currentIndex = Math.max(0, FEELS.findIndex((feel) => feel.id === currentId));
  const nextIndex = (currentIndex + delta + FEELS.length) % FEELS.length;
  return FEELS[nextIndex].id;
}

function selectedFeel(feelId) {
  return FEELS.find((feel) => feel.id === feelId) || null;
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = React.useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduce(media.matches);
    apply();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    media.addListener?.(apply);
    return () => media.removeListener?.(apply);
  }, []);

  return reduce;
}

/**
 * Orb Feel selector. Exactly one Feel at a time.
 * The orb is visual; full names live in the external legend.
 */
export default function FeelWheel({ feelId, onSelect, disabled }) {
  const [focusedId, setFocusedId] = React.useState(null);
  const buttonRefs = React.useRef({});
  const selected = selectedFeel(feelId);
  const reduceMotion = usePrefersReducedMotion();
  const idle = !selected;
  const animate = Boolean(selected) && !reduceMotion;
  const orbColor = selected?.color || NEUTRAL_ORB_COLOR;
  const noteColor = selected?.color || NEUTRAL_NOTE_COLOR;

  const focusFeel = (id) => {
    buttonRefs.current[id]?.focus();
  };

  const choose = (id) => {
    if (disabled) return;
    onSelect?.(id);
  };

  const onKeyDown = (event, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(id);
      return;
    }
    const delta = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    }[event.key];
    if (!delta) return;
    event.preventDefault();
    const nextId = moveFeel(id, delta);
    choose(nextId);
    focusFeel(nextId);
  };

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <div className="mx-auto w-full max-w-[20rem] overflow-visible bg-transparent px-1">
        <div
          data-testid="feel-orb"
          data-orb-color={orbColor}
          className={`feel-orb relative mx-auto aspect-square w-[78%] overflow-visible rounded-full bg-transparent ${
            animate ? 'feel-orb--live' : 'feel-orb--idle'
          }`}
          style={{
            '--feel-glow': orbColor,
            boxShadow: orbHalo([orbColor], { restrained: idle }),
          }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{ background: orbCoreBackground([orbColor]) }}
          />
          <div className="pointer-events-none absolute inset-[16%] z-[1] flex flex-col items-center justify-center overflow-hidden rounded-full px-2 text-center">
            {selected ? (
              <p
                data-testid="feel-orb-label"
                className="max-w-full text-sm font-semibold leading-snug text-white"
              >
                {selected.label}
              </p>
            ) : (
              <>
                <p data-testid="feel-orb-label" className="text-sm font-semibold leading-tight text-white">
                  How should it feel?
                </p>
                <p className="mt-1 text-xs text-white/70">Choose one</p>
              </>
            )}
          </div>

          <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
            {NOTE_SYMBOLS.map((symbol, index) => (
              <span
                key={symbol}
                className={`vibe-note absolute text-sm font-bold ${animate ? `vibe-note--live vibe-note--${index % 3}` : ''}`}
                style={{
                  top: NOTE_LAYOUT[index].top,
                  left: NOTE_LAYOUT[index].left,
                  color: noteColor,
                  opacity: selected ? 0.9 : 0.45,
                  textShadow: selected
                    ? `0 0 8px ${noteColor}99`
                    : `0 0 6px ${noteColor}33`,
                  animationDelay: animate ? `${index * 0.18}s` : undefined,
                }}
              >
                {symbol}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        role="radiogroup"
        aria-label="How should it feel?"
        className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {FEELS.map((feel, index) => {
          const isSelected = feelId === feel.id;
          const tabIndex = isSelected || (!feelId && index === 0) ? 0 : -1;
          const focused = focusedId === feel.id;
          return (
            <button
              key={feel.id}
              ref={(node) => {
                buttonRefs.current[feel.id] = node;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              tabIndex={tabIndex}
              onClick={() => choose(feel.id)}
              onKeyDown={(event) => onKeyDown(event, feel.id)}
              onFocus={() => setFocusedId(feel.id)}
              onBlur={() => setFocusedId((current) => (current === feel.id ? null : current))}
              className={`flex min-h-[44px] items-center gap-3 rounded-full border px-3 py-2 text-left text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-not-allowed ${
                isSelected
                  ? 'border-white bg-white/15'
                  : focused
                    ? 'border-white/70 bg-white/5'
                    : 'border-white/15 bg-white/5'
              }`}
            >
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: feel.color, boxShadow: `0 0 8px ${feel.color}` }}
              />
              <span className="min-w-0 flex-1 leading-tight">
                <span aria-hidden="true">{feel.symbol} </span>
                {feel.label}
              </span>
              {isSelected ? <span aria-hidden="true" className="text-xs">✓</span> : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
